// React and Mui Imports
import {
    Link,
    NavLink,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";

// Component Imports
import InlineSVG from "../inline-svg/inline-svg-component";
import SignIn from "../sign-in/sign-in";
import CustomDialog from "../dialog/dialog";

// mui icons imports
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Asset Imports (Icons and Logo)
import logo from "../../assets/vodafone-logo-icon.svg";
import HomeIcon from "../../assets/icons/solid/home.svg";
import HomeGradient from "../../assets/icons/gradient/home.svg";
import AdminIcon from "../../assets/icons/solid/admin.svg";
import AdminIconGradient from "../../assets/icons/gradient/admin.svg";
import GetHelpIcon from "../../assets/icons/solid/help-circle.svg";
import GetHelpIconGradient from "../../assets/icons/gradient/help-circle.svg";
import OnlineIcon from "../../assets/icons/solid/online.svg";
import OnlineIconGradient from "../../assets/icons/gradient/online.svg";
import burgerMenu from "../../assets/icons/solid/burger-menu.svg";
import CloseIcon from "../../assets/icons/solid/close.svg";
import ChevronRightIcon from "../../assets/icons/solid/chevron-right.svg";
import WarningFilledIcon from "../../assets/icons/solid/warning-filled.svg";

import { logout } from "../../services/auth.service";

// function to preload images and save them in browser cache
const preloadImage = (src) => {
    const img = new Image();
    img.src = src;
};

export default function Navbar() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    // Signin states
    const [otpSent, setOtpSent] = useState(false);
    const [otpNumberOfAttempts, setOtpNumberOfAttempts] = useState(3);
    const [invalidOtp, setInvalidOtp] = useState(false);

    // Vodacom URL Params
    const [searchParams] = useSearchParams();
    const loginPopup = searchParams.get("login");

    const [isSignedIn, setIsSignedIn] = useState(false);
    const [hoveredLink, setHoveredLink] = useState("");
    const [openMenu, setOpenMenu] = useState(false);

    const [alertInfo, setAlertInfo] = useState({
        showSignInAlert: false,
        signInAlertMessage: null,
        signInAlertSeverity: "warning",
    });

    // Sign in timer
    const [timer, setTimer] = useState(0);

    // Dialogs
    const [signInDialogOpen, setSignInDialogOpen] = useState(false);
    const [logoutWarningDialogOpen, setLogoutWarningDialogOpen] = useState(false);
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    useEffect(() => {
        if (loginPopup) {
            setSignInDialogOpen(true);
        }
    }, [loginPopup]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            setIsSignedIn(true);
        }
    }, []);

    // Handle dialog close event
    const handleDialogClose = (_, reason) => {
        if (reason && reason === "backdropClick") return;
        setLogoutWarningDialogOpen(false);
        setSignInDialogOpen(false);
        setOpenMenu(false);
        navigate("/");
    };

    // Handle dialog open event
    const handleLogoutWarningOpen = (event) => {
        setLogoutWarningDialogOpen(true);
    };

    const handleSignInDialogOpen = () => {
        setSignInDialogOpen(true);
    };

    const handleSignOut = async () => {
        try {
            await logout();
            localStorage.removeItem("token");
            setIsSignedIn(false);
            navigate("/");
            setLogoutWarningDialogOpen(false);
            setSignInDialogOpen(false);
            setOpenMenu(false);
        } catch (e) {
            localStorage.removeItem("token");
            setIsSignedIn(false);
            navigate("/");
            setLogoutWarningDialogOpen(false);
            setSignInDialogOpen(false);
            setOpenMenu(false);
        }
    };

    const handleSignIn = () => {
        if (isSignedIn) {
            handleLogoutWarningOpen();
        } else {
            handleSignInDialogOpen();
            setOpenMenu(false);
        }
    };

    // preload images
    useEffect(() => {
        // Preload hover images
        preloadImage(HomeGradient);
        preloadImage(GetHelpIconGradient);
        preloadImage(AdminIconGradient);
        preloadImage(OnlineIconGradient);
        preloadImage(CloseIcon);
    }, []);

    const handleDrawerOpen = () => {
        console.log("Open drawer");
    }
    return (
        <>
            <header
                className={`${openMenu ? "bg-screen-grey" : "bg-white"} sticky top-0 z-20 flex items-center justify-between shadow pl-2 md:px-16 py-4`}
            >
                {/* <Link to="/dashboard" className="md:ml-4 my-1"> */}
                <img src={logo} alt="Vodafone Logo" width={40} height={40} />
                {/* </Link> */}

                <nav className="ml-auto hidden md:flex items-center gap-x-6 transition-opacity md:mr-4">
                    {isSignedIn && (
                        <>
                            <NavLink
                                to="/dashboard"
                                end
                                className="flex items-center gap-x-1"
                                onMouseEnter={() => setHoveredLink("dashboard")}
                                onMouseLeave={() => setHoveredLink("")}
                                onFocus={() => setHoveredLink("dashboard")}
                                onBlur={() => setHoveredLink("")}
                            >
                                <InlineSVG
                                    src={
                                        hoveredLink === "dashboard"
                                            ? HomeGradient
                                            : location.pathname === "/dashboard"
                                                ? HomeGradient
                                                : HomeIcon
                                    }
                                    width={24}
                                    height={24}
                                    ariaHidden
                                />
                                <span className="text-base font-medium">Home</span>
                            </NavLink>
                            <NavLink
                                to="/dashboard/profile"
                                end
                                className="flex items-center gap-x-1"
                                onMouseEnter={() => setHoveredLink("/dashboard/profile")}
                                onMouseLeave={() => setHoveredLink("")}
                                onFocus={() => setHoveredLink("/dashboard/profile")}
                                onBlur={() => setHoveredLink("")}
                            >
                                <InlineSVG
                                    src={
                                        hoveredLink === "/dashboard/profile"
                                            ? AdminIconGradient
                                            : location.pathname === "/dashboard/profile"
                                                ? AdminIconGradient
                                                : AdminIcon
                                    }
                                    width={24}
                                    height={24}
                                    ariaHidden
                                />
                                <span className="text-base font-medium">Manage profile</span>
                            </NavLink>
                        </>
                    )}

                    {!isSignedIn && (
                        <NavLink
                            to="/"
                            end
                            className="flex items-center gap-x-1"
                            onMouseEnter={() => setHoveredLink("home")}
                            onMouseLeave={() => setHoveredLink("")}
                            onFocus={() => setHoveredLink("home")}
                            onBlur={() => setHoveredLink("")}
                        >
                            <InlineSVG
                                src={
                                    hoveredLink === "home"
                                        ? HomeGradient
                                        : location.pathname === "/"
                                            ? HomeGradient
                                            : HomeIcon
                                }
                                width={24}
                                height={24}
                                ariaHidden
                            />
                            <span className="text-base font-medium">Home</span>
                        </NavLink>
                    )}
                    {isSignedIn && (
                        <NavLink
                            to="/get-help"
                            end
                            className="flex items-center gap-x-1"
                            onMouseEnter={() => setHoveredLink("get-help")}
                            onMouseLeave={() => setHoveredLink("")}
                            onFocus={() => setHoveredLink("get-help")}
                            onBlur={() => setHoveredLink("")}
                        >
                            <InlineSVG
                                src={
                                    hoveredLink === "get-help"
                                        ? GetHelpIconGradient
                                        : location.pathname === "/get-help"
                                            ? GetHelpIconGradient
                                            : GetHelpIcon
                                }
                                width={24}
                                height={24}
                                ariaHidden
                            />
                            <span className="text-base font-medium">Get help</span>
                        </NavLink>
                    )}

                    <Link
                        className="flex items-center gap-x-1"
                        onClick={handleSignIn}
                        onMouseEnter={() => setHoveredLink("sign-in")}
                        onMouseLeave={() => setHoveredLink("")}
                        onFocus={() => setHoveredLink("sign-in")}
                        onBlur={() => setHoveredLink("")}
                    >
                        <InlineSVG
                            src={hoveredLink === "sign-in" ? OnlineIconGradient : OnlineIcon}
                            width={24}
                            height={24}
                            ariaHidden
                        />
                        <span className="text-base font-medium">
                            {isSignedIn ? "Sign out" : "Sign in"}
                        </span>
                    </Link>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={handleDrawerOpen}
                        sx={[false && { display: 'none' }]}
                    >
                    </IconButton>
                </nav>

                <IconButton
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    className="md:hidden block bg-transparent"
                    aria-label="mobile menu icon"
                    onClick={() => setOpenMenu(!openMenu)}
                >
                    <InlineSVG
                        className="fill-black"
                        src={openMenu ? CloseIcon : burgerMenu}
                        width={24}
                        height={24}
                        ariaHidden
                    />
                </IconButton>
            </header>

            {/* mobile nav */}
            {openMenu && (
                <nav className="fixed z-10 top-20 bg-screen-grey h-full w-full md:hidden flex flex-col items-center gap-6 px-2 py-6 transition-opacity">
                    {isSignedIn && (
                        <>
                            <NavLink
                                to="/dashboard"
                                end
                                className="flex items-center gap-x-2 w-full justify-between border-b border-b-grey-50 pb-6"
                                onFocus={() => setHoveredLink("dashboard")}
                                onBlur={() => setHoveredLink("")}
                                onClick={() => setOpenMenu(false)}
                            >
                                <div className="flex items-center gap-x-2">
                                    <InlineSVG
                                        src={
                                            hoveredLink === "dashboard"
                                                ? HomeGradient
                                                : location.pathname === "/dashboard"
                                                    ? HomeGradient
                                                    : HomeIcon
                                        }
                                        width={24}
                                        height={24}
                                        ariaHidden
                                    />
                                    <span className="text-base font-medium m-0">Home</span>
                                </div>

                                <InlineSVG
                                    src={ChevronRightIcon}
                                    width={16}
                                    height={16}
                                    ariaHidden
                                />
                            </NavLink>

                            <NavLink
                                to="/dashboard/profile"
                                end
                                className="flex items-center gap-x-2 w-full justify-between border-b border-b-grey-50 pb-6"
                                onFocus={() => setHoveredLink("dashboard/profile")}
                                onBlur={() => setHoveredLink("")}
                                onClick={() => setOpenMenu(false)}
                            >
                                <div className="flex items-center gap-x-2">
                                    <InlineSVG
                                        src={
                                            hoveredLink === "dashboard/profile"
                                                ? AdminIconGradient
                                                : location.pathname === "/dashboard/profile"
                                                    ? AdminIconGradient
                                                    : AdminIcon
                                        }
                                        width={24}
                                        height={24}
                                        ariaHidden
                                    />
                                    <span className="text-base font-medium m-0">
                                        Manage profile
                                    </span>
                                </div>
                                <InlineSVG
                                    src={ChevronRightIcon}
                                    width={16}
                                    height={16}
                                    ariaHidden
                                />
                            </NavLink>
                        </>
                    )}

                    <NavLink
                        to="/get-help"
                        end
                        className="flex items-center gap-x-2 w-full justify-between border-b border-b-grey-50 pb-6"
                        onFocus={() => setHoveredLink("get-help")}
                        onBlur={() => setHoveredLink("")}
                        onClick={() => setOpenMenu(false)}
                    >
                        <div className="flex items-center gap-x-2">
                            <InlineSVG
                                src={
                                    hoveredLink === "get-help"
                                        ? GetHelpIconGradient
                                        : location.pathname === "/get-help"
                                            ? GetHelpIconGradient
                                            : GetHelpIcon
                                }
                                width={24}
                                height={24}
                                ariaHidden
                            />
                            <span className="text-base font-medium m-0">Get help</span>
                        </div>
                        <InlineSVG
                            src={ChevronRightIcon}
                            width={16}
                            height={16}
                            ariaHidden
                        />
                    </NavLink>

                    <Link
                        className="flex items-center gap-x-2 w-full justify-between"
                        onClick={handleSignIn}
                        onFocus={() => setHoveredLink("sign-in")}
                        onBlur={() => setHoveredLink("")}
                    >
                        <div className="flex items-center gap-x-2">
                            <InlineSVG
                                src={
                                    hoveredLink === "sign-in" ? OnlineIconGradient : OnlineIcon
                                }
                                width={24}
                                height={24}
                                ariaHidden
                            />
                            <span className="text-base font-medium">
                                {isSignedIn ? "Sign out" : "Sign in"}
                            </span>
                        </div>
                        <InlineSVG
                            src={ChevronRightIcon}
                            width={16}
                            height={16}
                            ariaHidden
                        />
                    </Link>
                </nav>
            )}

            {/* sign in dialog  */}
            <CustomDialog
                fullScreen={fullScreen}
                maxWidth="sm"
                open={signInDialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="sign-dialog-title"
                disableEscapeKeyDown
                withAlert={alertInfo.showSignInAlert}
                alertSeverity={alertInfo.signInAlertSeverity}
                alertMessage={alertInfo.signInAlertMessage}
            >
                <DialogTitle id="sign-dialog-title">
                    <div className="md:hidden self-end">
                        <IconButton
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            className="bg-grey-50 w-8 h-8 rounded-full"
                            aria-label="close dialog"
                            onClick={handleDialogClose}
                        >
                            <CloseRoundedIcon />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent className="p-2 md:p-10">
                    <SignIn
                        onClose={handleDialogClose}
                        setAlertInfo={setAlertInfo}
                        otpSent={otpSent}
                        setOtpSent={setOtpSent}
                        otpNumberOfAttempts={otpNumberOfAttempts}
                        setOtpNumberOfAttempts={setOtpNumberOfAttempts}
                        invalidOtp={invalidOtp}
                        setInvalidOtp={setInvalidOtp}
                        setTimer={setTimer}
                        timer={timer}
                    />
                </DialogContent>
            </CustomDialog>

            {/*logout dialog */}
            <CustomDialog
                fullScreen={fullScreen}
                maxWidth="xs"
                open={logoutWarningDialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="logout-dialog-title"
            >
                <DialogTitle id="logout-dialog-title">
                    <div className="self-end">
                        <IconButton
                            disableRipple
                            className="bg-grey-50 w-8 h-8 rounded-full"
                            aria-label="close dialog"
                            onClick={handleDialogClose}
                        >
                            <CloseRoundedIcon />
                        </IconButton>
                    </div>
                    <InlineSVG
                        src={WarningFilledIcon}
                        width={80}
                        height={80}
                        ariaHidden
                        className="mb-4"
                    />
                    Sign out?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Any unsaved data will be lost. Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="md:justify-center">
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleDialogClose}
                    >
                        No, take me back
                    </Button>
                    <Button variant="contained" autoFocus onClick={handleSignOut}>
                        Yes, I'm sure
                    </Button>
                </DialogActions>
            </CustomDialog>
        </>
    );
}
